import { getSpecificParent } from '@apia/util';
import { dragleave } from '../events/Dragleave';
import { dragover } from '../events/Dragover';
import { dragstart } from '../events/Dragstart';
import { Drop } from '../events/Drop';
import { TPanelData, TSectionData } from '../types';

export class DragAndDrop {
  #unsuscribers: (() => void)[] = [];

  bootstrap = () => {
    this.#unsuscribers.forEach((unsuscribe) => unsuscribe());
    this.#unsuscribers = [];

    this.body.addEventListener('dragover', this.dragOver);
    this.body.addEventListener('drop', this.drop);
    this.#unsuscribers.push(() => {
      this.body.removeEventListener('dragover', this.dragOver);
      this.body.removeEventListener('drop', this.drop);
    });
    if (this.splash) {
      this.splash.addEventListener('dragleave', this.dragLeave);
      this.splash.addEventListener('dragenter', this.dragEnter);
      this.body.addEventListener('dragenter', this.dragEnter);
      this.#unsuscribers.push(() => {
        this.splash.removeEventListener('dragleave', this.dragLeave);
        this.splash.removeEventListener('dragenter', this.dragEnter);
        this.body.removeEventListener('dragenter', this.dragEnter);
      });
    }
    this.panels.map((el) => {
      const dragHandler = (ev: DragEvent) => {
        if ((ev.target as HTMLElement).closest('.drag_panel_container')) {
          this.dragStart(ev, el);
        }
      };
      (el as HTMLElement).addEventListener('dragstart', dragHandler);
      this.#unsuscribers.push(() => {
        (el as HTMLElement).removeEventListener('dragstart', dragHandler);
      });
      return el;
    });
  };
  panelData: TPanelData[] = [];
  minimap: HTMLElement | null = null;
  drawMinimap() {
    return;
  }
  timeout = -1;
  sectionData: TSectionData[] = [];
  body = document.querySelector('[data-layout]') as HTMLElement;
  splash = document.querySelector('[data-splash]') as HTMLElement;
  dragoverPosition: {
    x: string | number;
    y: string | number;
  } = { x: 0, y: 0 };
  shadowDivData: TPanelData | null = null;
  shadowDiv: HTMLDivElement | null = null;
  placeholderImage: HTMLElement | null = null;
  panels = [
    ...document.querySelectorAll('[data-panel][data-id]'),
  ] as HTMLElement[];
  currentScroll = document.scrollingElement?.scrollTop ?? 0;
  draggingPanel: HTMLElement | null = null;
  draggingPanelId = '';
  draggingPanelSectionId = '';
  currentBottomPanel: TPanelData | undefined = undefined;

  updateMinimap = () => {
    const frameElement = document.querySelector<HTMLElement>('.windowFrame');
    if (frameElement) {
      frameElement.style.top = `${
        (document.scrollingElement?.scrollTop ?? 0) *
        Number(frameElement.dataset.aspect)
      }px`;
    }
  };
  removePlaceholder = () => {
    this.clearTimeout();
    const falseDivs = document.querySelectorAll('.apia_panel_falseDiv');

    falseDivs.forEach((el) => {
      el.remove();
    });

    this.placeholderImage?.remove();
    this.placeholderImage = null;
  };
  clearTimeout = () => {
    clearTimeout(this.timeout);
  };
  debounceAppend = (target: HTMLElement, child: HTMLElement) => {
    if (!this.draggingPanelId) return;
    if (this.timeout === -1) {
      target.appendChild(child);
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      target.appendChild(child);
    }, 500) as unknown as number;
  };
  debounceBefore = (reference: Node, child: Node, container: HTMLElement) => {
    if (!this.draggingPanelId) return;
    if (this.timeout === -1) container.insertBefore(child, reference);
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      container.insertBefore(child, reference);
    }, 500) as unknown as number;
  };
  debounceAfter = (child: Node, reference: Node) => {
    if (!this.draggingPanelId) return;
    if (this.timeout === -1) (reference as HTMLElement).after(child);
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      (reference as HTMLElement).after(child);
    }, 500) as unknown as number;
  };
  dragOver = (event: Event) => {
    dragover(event, this);
    this.updateMinimap();
  };
  dragStart = (ev: DragEvent, el: Element) => {
    dragstart(ev, el, this);
  };
  dragLeave = (_ev: Event) => {
    dragleave(_ev, this);
  };
  dragEnter = (_ev: Event) => {
    _ev.preventDefault();
  };
  drop = (event: Event) => {
    Drop(event, this);
  };
  snapshot = () => {
    this.panelData = [];
    const innerPanels = (this.body as HTMLElement).querySelectorAll(
      '[data-panel][data-id]',
    );
    innerPanels.forEach((panel) => {
      if (panel.classList.contains('apia_panel_falseDiv')) {
        this.shadowDivData = {
          id: panel.getAttribute('data-id') ?? '',
          isShadow: true,
          panelBottom:
            panel.getBoundingClientRect().bottom + this.currentScroll,
          panelTop: panel.getBoundingClientRect().top + this.currentScroll,
          panelRight: panel.getBoundingClientRect().right,
          panelLeft: panel.getBoundingClientRect().left,
          panelWidth: panel.getBoundingClientRect().width,
          panelHeight: panel.getBoundingClientRect().height,
          row:
            getSpecificParent(
              panel as HTMLElement,
              (current) => !!current.dataset.locationid,
            )?.classList.contains('apia_section_row') ?? false,
          sectionId:
            getSpecificParent(
              panel as HTMLElement,
              (current) => !!current.dataset.locationid,
            )?.dataset.locationid ?? 0,
        };
      }
      if (
        this.panelData.every((innerPanel) => {
          return (
            innerPanel.id !== panel.getAttribute('data-id') &&
            !panel.classList.contains('apia_panel_falseDiv')
          );
        })
      ) {
        this.panelData.push({
          isShadow: panel.classList.contains('apia_panel_falseDiv'),
          panelBottom:
            panel.getBoundingClientRect().bottom + this.currentScroll,
          panelTop: panel.getBoundingClientRect().top + this.currentScroll,
          panelRight: panel.getBoundingClientRect().right,
          panelLeft: panel.getBoundingClientRect().left,
          panelWidth: panel.getBoundingClientRect().width,
          panelHeight: panel.getBoundingClientRect().height,
          row:
            getSpecificParent(
              panel as HTMLElement,
              (current) => !!current.dataset.locationid,
            )?.classList.contains('apia_section_row') ?? false,
          sectionId:
            getSpecificParent(
              panel as HTMLElement,
              (current) => !!current.dataset.locationid,
            )?.dataset.locationid ?? 0,
          id:
            getSpecificParent(
              panel as HTMLElement,
              (current) => !!current.dataset.id,
            )?.dataset.id ?? 0,
        });
      }
    });
    this.sectionData = [];
    const sections = this.body?.querySelectorAll('[data-locationid]');

    sections?.forEach((sec) => {
      if (
        this.sectionData.every(
          (innerSection) =>
            innerSection.sectionId !== sec.getAttribute('data-locationid'),
        )
      ) {
        this.sectionData.push({
          sectionBottom:
            sec.getBoundingClientRect().bottom + this.currentScroll,
          sectionLeft: sec.getBoundingClientRect().left,
          sectionTop: sec.getBoundingClientRect().top + this.currentScroll,
          sectionRight: sec.getBoundingClientRect().right,
          sectionWidth: sec.getBoundingClientRect().width,
          sectionHeight: sec.getBoundingClientRect().height,
          sectionId:
            getSpecificParent(
              sec as HTMLElement,
              (current) => !!current.dataset.locationid,
            )?.dataset.locationid ?? 0,
        });
      }
    });

    this.drawMinimap();
  };
  findBottomPanel = (x: number, y: number) => {
    const bottomPanel = this.panelData.find((panel) => {
      return (
        panel.panelBottom >= y &&
        panel.panelTop <= y &&
        panel.panelLeft <= x &&
        panel.panelRight >= x
      );
    });
    return bottomPanel;
  };
  findBottomSection = (x: number, y: number) => {
    const bottomSection = this.sectionData.find((sec) => {
      return (
        sec.sectionBottom >= y &&
        sec.sectionTop <= y &&
        sec.sectionLeft <= x &&
        sec.sectionRight >= x
      );
    });
    return bottomSection;
  };
}
